import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { appIconSize } from "web/constants";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  wrapper: {
    alignItems: "center",
    paddingTop: 16,
    paddingBottom: 8,
  },
  iconRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  deleteIcon: {
    position: "absolute",
    left: appIconSize + 12,
    top: -10,
    opacity: 0.75,
    zIndex: 1,
  },
  iconStack: {
    position: "relative",
    width: appIconSize,
    height: appIconSize,
    justifyContent: "center",
    alignItems: "center",
  },
  stackedIcon: {
    position: "absolute",
    top: 0,
    left: 0,
    width: appIconSize,
    height: appIconSize,
  },
  htmlInput: {
    display: "none",
  },
  fileName: {
    marginTop: 6,
    color: theme.color.darkGrey,
    textAlign: "center",
    maxWidth: 300,
    minWidth: 120,
  },
});

export default function CSVFileUpload({
  file,
  setFile,
  setFileBlob,
  onChange,
  canDelete = true,
  disabled = false,
  sizeMultiplier = { width: 1.1, height: 1.1 },
}) {
  const { showToast } = useContext(ToastContext);
  const { organizationColor } = useContext(OrganizationContext);

  const inputRef = useRef(null);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        icon: {
          width: appIconSize * sizeMultiplier.width,
          height: appIconSize * sizeMultiplier.height,
          opacity: 0.5,
          fill: organizationColor,
        },
        container: {
          width: appIconSize * sizeMultiplier.width,
          height: appIconSize * sizeMultiplier.height,
          justifyContent: "center",
          alignItems: "center",
        },
      }),
    [organizationColor, sizeMultiplier],
  );

  const handleInputChange = useCallback(
    (event) => {
      const eventFile = event.target.files?.[0];
      if (!eventFile) {
        return;
      }

      if (
        eventFile.type !== "text/csv" &&
        eventFile.type !== "application/vnd.ms-excel"
      ) {
        return showToast("Please upload a valid CSV file.", "error");
      }

      const maxSize = 1_000_000;
      if (eventFile.size > maxSize) {
        return showToast("CSV file too large (max 1MB).", "error");
      }

      onChange?.();
      setFileBlob?.(eventFile);
      setFile(eventFile.name);
    },
    [onChange, setFile, setFileBlob, showToast],
  );

  const handleDeletePress = useCallback(() => {
    setFile(null);
    setFileBlob?.(null);
    onChange?.();
  }, [setFile, setFileBlob, onChange]);

  return (
    <View style={classes.wrapper}>
      <View style={classes.iconRow}>
        <Pressable onPress={() => !disabled && inputRef.current.showPicker()}>
          <View style={stateClasses.container}>
            <View style={classes.iconStack}>
              <Icon
                name="file-upload-outline"
                color={organizationColor}
                style={classes.stackedIcon}
                size={appIconSize}
              />
            </View>
          </View>
          <input
            style={classes.htmlInput}
            type="file"
            accept=".csv,text/csv"
            onChange={handleInputChange}
            ref={inputRef}
          />
        </Pressable>
        {canDelete && !disabled && file && (
          <PressableContainer
            rootStyle={classes.deleteIcon}
            onPress={handleDeletePress}
          >
            <Icon name="close-thick" color={theme.color.red} size={12} />
          </PressableContainer>
        )}
      </View>
      {file && <Text style={classes.fileName}>{file}</Text>}
    </View>
  );
}
